import clsx from "clsx";
import * as React from "react";
import type { JSX } from "react";
import { Input as UIInput } from "@/components/ui/input";
import { INPUT_COLORS } from "@/enums/library";
import { GenericInputTypes, Status } from "@/types/common/library";
import { formatPhoneNumber, genericNumberPattern } from "@/utils/library/formatters";
import Button from "./Button";
import Icon, { ICON_SIZES, IconProps } from "./Icon";
import InputLabel from "./InputLabel";

type InputType =
  | React.HTMLInputTypeAttribute
  | "passwordShow"
  | "phone"
  | "url"
  | "ssn"
  | "user"
  | "odometer"
  | "vin"
  | "garage"
  | "address";

export type InputProps = {
  type: InputType;
  icon?: JSX.Element;
  tooltipText?: React.ReactNode;
  wrapperClassName?: string;
  /** When true, hides error styling while the input is focused */
  hideErrorOnFocus?: boolean;
  /** When true, hides the default icon for the input type */
  hideIcon?: boolean;
} & GenericInputTypes;

const ICON_DEFAULTS: Pick<IconProps, "size" | "className"> = {
  size: ICON_SIZES.LG,
  className: "w-6",
};

type InputTypeConfig = (type: InputType) => {
  type: React.HTMLInputTypeAttribute;
  icon?: JSX.Element;
  pattern?: string;
};

const getInputTypeConfig: InputTypeConfig = (type) => {
  switch (type) {
    case "email":
      return {
        type,
        icon: <Icon icon="envelope" {...ICON_DEFAULTS} />,
      };
    case "url":
      return {
        type: "text",
        icon: <Icon icon="globe" {...ICON_DEFAULTS} />,
      };
    case "ssn":
      return {
        type: "number",
        icon: <Icon icon="user-vneck-hair" {...ICON_DEFAULTS} />,
      };
    case "user":
      return {
        type: "text",
        icon: <Icon icon="user-crown" {...ICON_DEFAULTS} />,
      };
    case "password":
      return {
        type,
        icon: <Icon icon="key" {...ICON_DEFAULTS} />,
      };
    case "passwordShow":
      return {
        type: "text",
        icon: <Icon icon="key" {...ICON_DEFAULTS} />,
      };
    case "odometer":
      return {
        type: "number",
        icon: <Icon icon="gauge-low" {...ICON_DEFAULTS} />,
      };
    case "vin":
      return {
        type: "text",
        icon: <Icon icon="car-side" {...ICON_DEFAULTS} />,
      };
    case "garage":
      return {
        type: "text",
        icon: <Icon icon="garage-car" {...ICON_DEFAULTS} />,
      };
    case "address":
      return {
        type: "text",
        icon: <Icon icon="house-chimney" {...ICON_DEFAULTS} />,
      };
    case "phone":
      return {
        type: "tel",
        icon: <Icon icon="phone" {...ICON_DEFAULTS} />,
      };
    case "numberGeneric":
      return {
        type: "tel",
        pattern: genericNumberPattern,
      };
    default:
      return {
        type,
      };
  }
};

const getInputColor = ({ error, warning, success }: Status): INPUT_COLORS | undefined => {
  if (error) {
    return INPUT_COLORS.ERROR;
  }

  if (warning) {
    return INPUT_COLORS.WARNING;
  }

  if (success) {
    return INPUT_COLORS.SUCCESS;
  }

  return undefined;
};

const Input: React.FC<InputProps> = ({
  label,
  icon,
  success,
  error,
  warning,
  clearable = false,
  type = "text",
  clean = false,
  ["data-testid"]: dataTestId,
  className,
  tooltipText,
  hideErrorOnFocus = false,
  hideIcon = false,
  helperText,
  wrapperClassName,
  ...otherProps
}) => {
  const [phoneVal, setPhoneVal] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const showRequiredLabel = !!label && !!otherProps.required;

  // Sanitize value to avoid React warning about null values
  const sanitizedValue = otherProps.value === null ? "" : otherProps.value;
  const {
    type: updatedType,
    icon: updatedIcon,
    pattern: updatedPattern,
  } = getInputTypeConfig(type);

  // When hideErrorOnFocus is enabled, suppress error display while focused
  const shouldHideError = hideErrorOnFocus && isFocused;
  const effectiveError = shouldHideError ? false : error;
  const effectiveHelperText = shouldHideError ? undefined : helperText;

  const phoneChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    setPhoneVal(formattedPhoneNumber);
    otherProps.onChange?.({
      ...e,
      target: {
        ...e.target,
        value: formattedPhoneNumber,
      },
    });
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    otherProps.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    otherProps.onBlur?.(e);
  };

  return (
    <div className={wrapperClassName}>
      {label && (
        <InputLabel
          data-testid={dataTestId && `${dataTestId}_label`}
          htmlFor={otherProps.id}
          value={showRequiredLabel ? `${label} *` : label}
          tooltipText={tooltipText}
        />
      )}
      <UIInput
        type={updatedType}
        data-testid={dataTestId}
        icon={clean || hideIcon ? undefined : icon || updatedIcon}
        pattern={updatedPattern}
        {...otherProps}
        value={sanitizedValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={clsx(
          "[&>input]:bg-white dark:[&>input]:bg-gray_comp !rounded-lg [&>input]:rounded-[inherit]",
          getInputColor({ error: effectiveError, warning, success }) === INPUT_COLORS.ERROR &&
            "border-red-500",
          getInputColor({ error: effectiveError, warning, success }) === INPUT_COLORS.WARNING &&
            "border-yellow-500",
          getInputColor({ error: effectiveError, warning, success }) === INPUT_COLORS.SUCCESS &&
            "border-green-500",
          className
        )}
        {...(type === "phone" && {
          onChange: phoneChangeHandler,
          value: sanitizedValue || phoneVal,
        })}
      />
      {effectiveHelperText && (
        <p
          className={clsx(
            "mt-1 text-xs",
            effectiveError && "text-red-600",
            warning && "text-yellow-700",
            success && "text-green-700",
            !effectiveError && !warning && !success && "text-600"
          )}
        >
          {effectiveHelperText}
        </p>
      )}
      {clearable && !!otherProps.onChange && (
        <div className={clsx("float-right", "mt-[-42px]", "relative")}>
          <Button
            type="none"
            className="!p-[0.6rem]"
            onClick={() =>
              otherProps.onChange?.({
                currentTarget: {
                  value: "",
                },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          >
            <Icon icon="xmark" {...ICON_DEFAULTS} />
          </Button>
        </div>
      )}
    </div>
  );
};
export default Input;
